import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { PriceUnitComponent } from '../../components/price-unit/price-unit.component';
import { UiFormFieldBuilderModule } from '@digipay/ui-form-field-builder';
import { takeUntil } from 'rxjs';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { PaymentHandlerService } from '../../services/payment-handler.service';
import { BaseComponent } from '../../../../components/core/components/base/base.component';
import {
  IAddPasswordRouteState
} from '../../../makna-authentication/containers/add-password/models/add-password-route-state.interface';
import { IFundProfileModel } from '../../../funds/models/fund-profile.model';
import { User } from '../../../../data-access/models/user.model';
import { FormErrorStatus } from '../../../../data-access/models/form-error-status.model';
import { ProfileResponse } from '../../../../data-access/models/profile-response.model';
import { ErrorService } from '../../../../components/core/services/error.service';
import { EpdfType } from '../../../../components/core/models/instruments.enum';
import { ProfileService } from '../../../../components/core/services/profile.service';
import { FundDataService } from '../../../../components/core/services/fund-data.service';
import { numberToString } from '../../../../components/utils/number-to-string';
import { EIntrackEventName } from '../../../../components/core/models/intrack-event-name.enum';
import {
  CROWD_LIST_ROUTE,
  INVESTMENT_LIST_ROUTE,
  PROSPECTUS_ROUTE,
  RESULT_ROUTE,
  SEJAM_ERROR_ROUTE
} from '../../../../data-access/constants/app-routes';
import { IPayment } from '../../../../components/core/models/payment.interface';
import { ErrorCodes } from '../../../../data-access/enums/error-codes';
import { IProspectusRouteState } from '../../../../components/core/models/prospectus-route-state.interface';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { RouteStateService } from '@client-monorepo/common/utilities';
import { CrowdFundingService } from '../../../crowds/services/crowd-funding.service';
import { CrowdFundingModel } from '../../../crowds/models/crowd-funding.model';
import { switchMap } from 'rxjs/operators';
import { ICrowdFundingPurchaseData } from '../../../crowds/models/crowd-funding-purchase-data';
import { PipesModule } from '@digipay/ng-lib-pipes';
import { IAutoFill } from '../../models/purchase-auto-fill.model';
import { NgxAppBarComponent } from '@digipay/ngx-app-bar';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { NgxCheckboxComponent } from '@digipay/ngx-checkbox';
import { NgxCalloutComponent } from '@digipay/ngx-callout';
import { DecimalPipe, NgClass } from '@angular/common';
import { SpinnerComponent } from '../../../../shared/components/spinner/spinner.component';
import { NgxBottomSheetService } from '@digipay/ngx-bottom-sheet';
import { DelayBottomSheetComponent } from '../../components/delay-bottom-sheet/delay-bottom-sheet.component';

@Component({
  selector: 'app-purchase',
  templateUrl: './purchase.component.html',
  styleUrls: ['./purchase.component.scss'],
  standalone: true,
  imports: [
    MatCheckboxModule,
    NgxButtonComponent,
    PriceUnitComponent,
    ReactiveFormsModule,
    UiFormFieldBuilderModule,
    NgxAppBarComponent,
    PipesModule,
    NgxCheckboxComponent,
    NgxCalloutComponent,
    DecimalPipe,
    SpinnerComponent,
    NgClass
  ]
})
export class PurchaseComponent extends BaseComponent implements OnInit {
  form: FormGroup;
  pdfType = EpdfType;
  state = signal<IAddPasswordRouteState | undefined>(undefined);
  agreementChecked = signal<boolean>(false);
  showAgreementCheckbox = signal<boolean>(true);
  userInfo = signal<User | undefined>(undefined);
  isLoading = signal<boolean>(true);
  tomanValue = signal<string | undefined>(undefined);
  unitCount = signal<number>(0);
  assetData = signal<IFundProfileModel | undefined>(undefined);
  showError = signal<FormErrorStatus>('hidden');
  userProfile = signal<ProfileResponse | undefined>(undefined);
  onlineNav = signal<number | undefined>(undefined);
  symbol = signal<string | undefined>(undefined);
  buttonLoading = signal<boolean>(false);
  isCrowd = signal<boolean>(false);
  crowd = signal<CrowdFundingModel | undefined>(undefined);
  crowdData = signal<ICrowdFundingPurchaseData | undefined>(undefined);
  autoCompleteButtons = signal<IAutoFill[]>([
    {
      amount: 50000000,
      selected: false
    },
    {
      amount: 150000000,
      selected: false
    },
    {
      amount: 300000000,
      selected: false
    },
    {
      amount: 500000000,
      selected: false
    }
  ]);

  private bottomSheetService = inject(NgxBottomSheetService);
  private errorService = inject(ErrorService);
  private navigationService = inject(WealthNavigationService);
  private routeState = inject(RouteStateService);
  private route = inject(ActivatedRoute);
  private formBuilder = inject(FormBuilder);
  private profileService = inject(ProfileService);
  private fundDataService = inject(FundDataService);
  private crowdFundingService = inject(CrowdFundingService);
  private paymentHandlerService = inject(PaymentHandlerService);
  private eventService = inject(NgxEventTrackerService);
  private selectedAmount = signal<IAutoFill | undefined>(undefined);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.checkExpiredData();
    this.symbol.set(this.route.snapshot.params['id']);
    this.state.set(this.routeState.getAll());
    this.form = this.formBuilder.group({
      amount: this.state()?.amount || ''
    });
    window.onpageshow = function(event: PageTransitionEvent) {
      if (event.persisted) {
        location.reload();
        return;
      }
    };
    this.checkLoading();
    this.handleStateData();
    this.eventHandler(EIntrackEventName.FUND_BUY_PAGE);
    this.amountValueHandler();
    this.getData();
  }

  isValid() {
    let minValue = 0;
    let maxValue = 0;

    if (this.assetData()) {
      minValue = this.assetData().minBuyableAmount;
      maxValue = this.assetData().maxBuyableAmount;
    } else if (this.crowdData()) {
      minValue = this.crowdData().minimumAllowedPrice;
      maxValue = this.crowdData().maximumAllowedPrice;
    } else {
      return false;
    }
    return this.form.value.amount >= minValue && (maxValue ? this.form.value.amount <= maxValue : true);
  }

  agreementView(pdfType: EpdfType) {
    const state: IProspectusRouteState = {
      symbol: this.symbol(),
      backToProfile: false,
      pdfType,
      type: 'buy',
      amount: this.form.value.amount,
      investmentType: this.crowdData() ? this.crowdData().investmentType : this.assetData()?.investmentType,
      agreementChecked: this.agreementChecked()
    };

    this.navigationService.navigateWithState([PROSPECTUS_ROUTE], {
      state: state
    });
  }

  onBackHandler() {
    const query = {
      type: this.state().type || this.assetData().type
    };
    if (this.state()['referrer']) {
      query['referrer'] = this.state()['referrer'];
    }
    if (this.state()['backTo']) {
      this.navigationService.navigate([INVESTMENT_LIST_ROUTE, this.symbol()], {
        queryParams: query
      });
    } else {
      if (this.crowd()) {
        this.navigationService.navigate([CROWD_LIST_ROUTE, this.symbol()], {
          queryParams: query
        });
      } else {
        this.navigationService.navigate([INVESTMENT_LIST_ROUTE], {
          queryParams: query
        });
      }
    }
  }

  onToggleAgreement(val: any) {
    this.agreementChecked.set(val);
  }

  handleAutoFill(button: IAutoFill) {
    if (button !== this.selectedAmount()) {
      this.selectedAmount.set(button);
      this.autoCompleteButtons().forEach((btn) => {
        btn.amount === this.selectedAmount().amount ? (btn.selected = true) : (btn.selected = false);
      });
      this.form.controls['amount'].setValue(button.amount);
    } else {
      this.selectedAmount.set(undefined);
      this.form.controls['amount'].setValue(null);
      this.tomanValue.set(undefined);
      this.autoCompleteButtons().forEach((btn) => {
        btn.selected = false;
      });
    }
  }

  private checkLoading() {
    this.paymentHandlerService.loading$.pipe(takeUntil(this.destroyObservable)).subscribe((res) => {
      this.buttonLoading.set(res);
    });
  }

  private handleStateData() {
    if (this.state()?.amount) {
      this.tomanValue.set(numberToString(+this.state()?.amount / 10));
      this.showError.set('hidden');
    }
    if (this.state()?.agreementChecked) {
      this.agreementChecked.set(this.state()?.agreementChecked);
    }
  }

  private getData() {
    if (this.route.snapshot.queryParams['crowdFunding']) {
      this.isCrowd.set(true);
      this.crowdFundingService
        .getCrowdProject(this.symbol())
        .pipe(
          switchMap((res) => {
            if (res?.success) {
              this.crowd.set(res.result);
            }
            return this.crowdFundingService.getCrowdProfile(this.symbol());
          }),
          takeUntil(this.destroyObservable)
        )
        .subscribe((res) => {
          this.crowdData.set(res.result);
          console.log(this.crowdData());
          this.showAgreementCheckbox.set(false);
          this.isLoading.set(false);
        });
    } else {
      this.fundDataService
        .getFundBySymbol(this.symbol())
        .pipe(
          switchMap((fund) => {
            if (fund?.success) {
              this.assetData.set(fund.result);
            }
            if (this.form.value.amount) {
              this.unitCount.set(Math.floor(this.form.value.amount / this.assetData().purchaseNav));
            }

            return this.profileService.getProfile();
          }),
          takeUntil(this.destroyObservable)
        )
        .subscribe((profile) => {
          if (profile?.success) {
            this.userProfile.set(profile.result);
          }

          this.isLoading.set(false);
        });
    }
  }

  private checkExpiredData() {
    if (this.route.snapshot.queryParams['expired']) {
      this.errorService.setErrorPageExpired(false);
      this.navigationService.navigateWithQueryParams([RESULT_ROUTE], {
        queryParams: {
          incomplete: true,
          symbol: this.route.snapshot.queryParams['symbol']
        }
      });
      return;
    }
  }

  private eventHandler(eventName: string) {
    const eventData = {
      eventName: eventName,
      eventData: {
        amount: this.state()?.amount ?? 0,
        units: this.state()?.units ?? 0,
        fundId: this.state()?.symbol ?? 'نامشخص'
      }
    };
    this.eventService.sendEvent(eventData);
  }

  private amountValueHandler() {
    this.form.controls['amount'].valueChanges.subscribe((res) => {
      if (res) {
        this.amountChanged(res);
      }
    });
  }

  private verifyCustomerAndPaymentHandler() {
    this.fundDataService.verifyCustomer(this.symbol(), this.crowdData()?.investmentType === 'CrowdFund').subscribe((verifyResult) => {
      if (verifyResult?.success) {
        const payment: IPayment = this.generatePaymentData();
        const state = this.generateStateDate();
        this.paymentHandlerService.setPayment(payment);
        this.paymentHandlerService.handleState(verifyResult.result.state, state);
      } else {
        // ! Customer is not sejami
        if (verifyResult?.error?.code === ErrorCodes.CustomerIsNotSejami) {
          this.eventHandler(EIntrackEventName.SEJAM_SERVICE_ERROR);
          this.navigationService.navigate([SEJAM_ERROR_ROUTE], {
            state: {
              title: verifyResult.error.title,
              description: verifyResult.error.description
            }
          });
        }
      }
    });
  }

  getVerifyResult() {
    if (this.assetData().type === 'Gold') {
      this.bottomSheetService.openBottomSheet(DelayBottomSheetComponent, {
        data: { isPurchase: true }
      });
      this.bottomSheetService.onClose.pipe(takeUntil(this.destroyObservable)).subscribe(() => {
        const outputData = this.bottomSheetService.outputData();
        if (outputData) {
          this.buttonLoading.set(true);
          this.verifyCustomerAndPaymentHandler();
        }
      });
    } else {
      this.buttonLoading.set(true);
      this.verifyCustomerAndPaymentHandler();
    }
  }

  private generatePaymentData(): IPayment {
    return {
      symbol: this.crowdData() ? this.crowdData().symbol : this.assetData().symbol,
      amount: this.form.value.amount,
      agreementChecked: this.agreementChecked(),
      instrumentUnit: this.crowdData() ? 1 : this.unitCount(),
      units: this.crowdData() ? 1 : this.unitCount(),
      type: this.crowdData() ? this.crowdData().type : this.assetData().type,
      assetData: this.crowdData() ?? this.assetData()
    };
  }

  private generateStateDate() {
    return {
      symbol: this.crowdData() ? this.crowdData().symbol : this.assetData().symbol,
      type: this.crowdData() ? this.crowdData().type : this.assetData().type,
      agreementChecked: this.agreementChecked(),
      amount: this.form.value.amount,
      investmentType: this.crowdData() ? this.crowdData().investmentType : this.assetData()?.investmentType,
      assetData: this.crowdData() ? this.crowdData() : this.assetData(),
      unitCount: this.crowdData() ? 1 : this.unitCount()
    };
  }

  private amountChanged(val: number) {
    this.showError.set('hidden');
    this.tomanValue.set(numberToString(val / 10));
    if (!this.isCrowd()) {
      this.unitCount.set(this.assetData() ? Math.floor(val / this.assetData()?.purchaseNav) : 0);
    }
  }
}
